import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import type { DirectoryContact, Enterprise, Page } from '../lib/types';
import { relativeDays, titleCase } from '../lib/format';
import { useSession } from '../lib/session';
import { ImportDialog } from '../features/ImportDialog';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  ErrorNotice,
  Input,
  Loading,
  PageHeader,
  SectionNote,
  Select,
  StatTile,
  Table,
  Td,
  Th,
  Tr,
} from '../components/ui';

/**
 * The organisation-level contact directory (design 0001).
 *
 * One row per *person*, not per account link. Wei Chen is on both J&J MedTech
 * India and APAC; showing him twice would answer "how many relationships do we
 * have" when the question on this screen is "who do we know".
 *
 * Cadence is therefore aggregated to the person as their worst outstanding gap,
 * labelled with the account it belongs to — being on rhythm with a sponsor on
 * one account says nothing about the account where you have gone quiet.
 */
export function ContactsPage() {
  const { me } = useSession();
  const [importing, setImporting] = useState(false);
  const [q, setQ] = useState('');
  const [enterpriseId, setEnterpriseId] = useState('');
  const [seniorityLevel, setSeniorityLevel] = useState('');
  const [behindCadence, setBehindCadence] = useState(false);
  const [sort, setSort] = useState('seniority');

  const query = useQuery({
    queryKey: ['contacts-directory', { q, enterpriseId, seniorityLevel, behindCadence, sort }],
    queryFn: () =>
      api.get<Page<DirectoryContact>>('/contacts', {
        q: q || undefined,
        enterpriseId: enterpriseId || undefined,
        seniorityLevel: seniorityLevel || undefined,
        behindCadence: behindCadence || undefined,
        sort,
        limit: 200,
      }),
  });

  const enterprises = useQuery({
    queryKey: ['enterprises'],
    queryFn: () => api.get<Page<Enterprise>>('/enterprises', { limit: 200 }),
  });

  const contacts = query.data?.data ?? [];
  const behind = contacts.filter((c) => c.worstGap).length;
  const executives = contacts.filter((c) => c.topSeniorityRank === 0).length;
  const departed = contacts.filter((c) => c.status !== 'active').length;

  return (
    <>
      <PageHeader
        title="Contacts"
        description="Everyone we know at the customers you can see. A person appears once, however many accounts they are on."
        actions={
          me.capabilities.canCreateContacts && (
            <Button size="sm" variant="secondary" onClick={() => setImporting(true)}>
              Import
            </Button>
          )
        }
        meta={
          <div className="flex flex-wrap items-end gap-2">
            <Input
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder="Search name, email, title, account"
              className="w-64"
            />
            <Select value={enterpriseId} onChange={(event) => setEnterpriseId(event.target.value)}>
              <option value="">All enterprises</option>
              {(enterprises.data?.data ?? []).map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </Select>
            <Select
              value={seniorityLevel}
              onChange={(event) => setSeniorityLevel(event.target.value)}
            >
              <option value="">All seniority</option>
              <option value="executive">Executive</option>
              <option value="senior">Senior</option>
              <option value="working">Working</option>
            </Select>
            <Select value={sort} onChange={(event) => setSort(event.target.value)}>
              <option value="seniority">Seniority first</option>
              <option value="outreach">Most overdue first</option>
              <option value="name">Name</option>
              <option value="enterprise">Enterprise</option>
            </Select>
            <Checkbox
              checked={behindCadence}
              label="Behind on outreach"
              onChange={(event) => setBehindCadence(event.target.checked)}
            />
          </div>
        }
      />
      {importing && (
        <ImportDialog
          entity="contacts"
          label="contacts"
          invalidate={[['contacts-directory'], ['accounts']]}
          onClose={() => setImporting(false)}
        />
      )}

      {query.isLoading && <Loading />}
      {query.error && <ErrorNotice error={query.error} onRetry={() => query.refetch()} />}

      {query.data && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile label="People" value={query.data.total} />
            <StatTile
              label="Behind on outreach"
              value={behind}
              tone={behind > 0 ? 'warning' : undefined}
            />
            <StatTile label="Executives" value={executives} />
            <StatTile label="No longer active" value={departed} />
          </div>

          {contacts.length === 0 ? (
            <EmptyState
              title="No contacts match"
              description="Contacts are added on an account, under its Contacts tab."
            />
          ) : (
            <>
              <Card padded={false}>
                <Table>
                  <thead>
                    <tr>
                      <Th>Name</Th>
                      <Th>Enterprise</Th>
                      <Th>Accounts</Th>
                      <Th>Seniority</Th>
                      <Th>Outreach</Th>
                      <Th>Last reply</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((contact) => {
                      const top = contact.accounts.reduce((best, link) =>
                        link.seniorityLevel === 'executive' ||
                        (link.seniorityLevel === 'senior' && best.seniorityLevel === 'working')
                          ? link
                          : best,
                      );
                      return (
                        <Tr key={contact.id}>
                          <Td>
                            <span className="font-medium text-ink">
                              {contact.firstName} {contact.lastName}
                            </span>
                            {contact.status !== 'active' && (
                              <Badge tone="neutral" className="ml-2">
                                {titleCase(contact.status)}
                              </Badge>
                            )}
                            <span className="mt-0.5 block text-xs text-ink-2">
                              {contact.title ?? '—'}
                            </span>
                            {contact.email && (
                              <span className="block font-mono text-[11px] text-muted">
                                {contact.email}
                              </span>
                            )}
                          </Td>
                          <Td className="text-ink-2">{contact.enterpriseName}</Td>
                          <Td>
                            <span className="flex flex-col gap-0.5">
                              {contact.accounts.map((link) => (
                                <Link
                                  key={link.accountId}
                                  to={`/accounts/${link.accountId}?tab=contacts`}
                                  className="text-xs text-accent hover:underline"
                                >
                                  {link.accountName}
                                  <span className="ml-1 text-muted">
                                    {titleCase(link.relationshipType)}
                                  </span>
                                </Link>
                              ))}
                            </span>
                          </Td>
                          <Td>
                            <Badge
                              tone={top.seniorityLevel === 'executive' ? 'info' : 'neutral'}
                              dot={false}
                            >
                              {titleCase(top.seniorityLevel)}
                            </Badge>
                          </Td>
                          <Td className="text-xs">
                            {contact.worstGap ? (
                              <>
                                <Badge
                                  tone={
                                    contact.worstGap.severity === 'high' ? 'serious' : 'warning'
                                  }
                                >
                                  {contact.worstGap.overdueByDays}d overdue
                                </Badge>
                                <span className="mt-0.5 block text-[11px] text-muted">
                                  {contact.accountCount > 1
                                    ? `on ${contact.worstGap.accountName}`
                                    : contact.worstGap.daysSinceOutreach === null
                                      ? 'never contacted'
                                      : `last ${contact.worstGap.daysSinceOutreach}d ago`}
                                </span>
                              </>
                            ) : contact.status !== 'active' ? (
                              <span className="text-muted">—</span>
                            ) : (
                              <span className="text-ink-2">On rhythm</span>
                            )}
                          </Td>
                          <Td className="text-xs text-ink-2">
                            {contact.lastResponseAt ? relativeDays(contact.lastResponseAt) : '—'}
                          </Td>
                        </Tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Card>
              <div className="mt-3">
                <SectionNote>
                  Outreach measures what we sent — an outbound email or a meeting. A reply is
                  tracked separately, because their silence and our neglect are different problems.
                  Someone on several accounts shows their worst gap; open the account to see each
                  relationship on its own rhythm.
                </SectionNote>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}
